import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../firebase/config";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const formatNotificationTime = (createdAt) => {
  if (!createdAt?.seconds) return "Just now";

  const date = new Date(createdAt.seconds * 1000);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

const getNotificationIcon = (type) => {
  if (type === "report_status") return "🚩";
  if (type === "chat") return "💬";
  if (type === "product") return "🛒";
  if (type === "bid") return "💰";
  if (type === "event" || type === "reservation") return "🎪";
  if (type === "listing") return "🌾";
  if (type === "product_request") return "🛒";
  if (type === "product_sale_status") return "✅";
  if (type === "day_job_application") return "🛠️";
  if (type === "day_job_status") return "📋";
  if (type === "product_review") return "⭐";
  return "🔔";
};

const getNotificationRoute = (item) => {
  if (item.route) return item.route;

  if (item.type === "report_status") return "/home";
  if (item.type === "chat" && item.conversationId) return `/chat/${item.conversationId}`;
  if (item.type === "product") return "/home";
  if (item.type === "bid" || item.type === "field_bid" || item.type === "day_job_application") {
    return "/jobs";
  }
  if (item.type === "event" || item.type === "reservation") return "/home";

  return "/home";
};

const getNotificationSortValue = (item) => {
  if (item.createdAt?.seconds) return item.createdAt.seconds * 1000;
  if (item.createdAt instanceof Date) return item.createdAt.getTime();
  return 0;
};

const PRIMARY_NOTIFICATION_FIELD = "toUserId";
const LEGACY_NOTIFICATION_FIELD = "recipientId";

const NotificationBell = ({ user }) => {
  const navigate = useNavigate();
  const bellRef = useRef(null);
  const isAuthenticated = Boolean(user?.uid);
  const notificationSourcesRef = useRef({});

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    notificationSourcesRef.current = {};

    const mergeNotificationSources = () => {
      const merged = new Map();

      Object.values(notificationSourcesRef.current)
        .flat()
        .forEach((item) => {
          merged.set(item.id, item);
        });

      setNotifications(
        Array.from(merged.values())
          .sort((a, b) => getNotificationSortValue(b) - getNotificationSortValue(a))
          .slice(0, 30)
      );
    };

    let cancelled = false;

    const loadLegacyRecipientNotifications = async () => {
      const legacyQuery = query(
        collection(db, "notifications"),
        where(LEGACY_NOTIFICATION_FIELD, "==", user.uid),
        limit(30)
      );

      try {
        const snapshot = await getDocs(legacyQuery);
        if (cancelled) return;

        notificationSourcesRef.current[LEGACY_NOTIFICATION_FIELD] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        mergeNotificationSources();
      } catch (error) {
        if (cancelled) return;

        notificationSourcesRef.current[LEGACY_NOTIFICATION_FIELD] = [];
        mergeNotificationSources();
        if (error.code !== "permission-denied") {
          console.error("Notification bell legacy recipient lookup failed:", error);
        }
      }
    };

    const notifQuery = query(
      collection(db, "notifications"),
      where(PRIMARY_NOTIFICATION_FIELD, "==", user.uid),
      limit(30)
    );

    const unsub = onSnapshot(
      notifQuery,
      (snapshot) => {
        notificationSourcesRef.current[PRIMARY_NOTIFICATION_FIELD] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        mergeNotificationSources();
        setLoading(false);
      },
      (error) => {
        notificationSourcesRef.current[PRIMARY_NOTIFICATION_FIELD] = [];
        mergeNotificationSources();
        if (error.code !== "permission-denied") {
          console.error("Notification bell listener failed:", error);
        }
        setLoading(false);
      }
    );

    loadLegacyRecipientNotifications();

    return () => {
      cancelled = true;
      unsub();
    };
  }, [isAuthenticated, user?.uid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!bellRef.current) return;
      if (!bellRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((item) => item.read !== true).length,
    [notifications]
  );

  const markOneRead = async (item) => {
    if (item.read === true) return;

    try {
      await updateDoc(doc(db, "notifications", item.id), {
        read: true,
      });
    } catch (error) {
      console.error("Failed marking notification read:", error);
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter((item) => item.read !== true);

    try {
      await Promise.all(
        unread.map((item) =>
          updateDoc(doc(db, "notifications", item.id), {
            read: true,
          })
        )
      );
    } catch (error) {
      console.error("Failed marking notifications read:", error);
    }
  };

  const openNotification = async (item) => {
    await markOneRead(item);

    const route = getNotificationRoute(item);
    setOpen(false);
    navigate(route);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="notification-bell-wrap" ref={bellRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Open notifications"
      >
        🔔
        {unreadCount > 0 && (
          <span className="notification-bell-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <div>
              <strong>Notifications</strong>
              <p>{unreadCount} unread</p>
            </div>

            {unreadCount > 0 && (
              <button type="button" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading && <p className="notification-empty">Loading...</p>}

            {!loading && notifications.length === 0 && (
              <p className="notification-empty">No notifications yet.</p>
            )}

            {!loading &&
              notifications.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`notification-item ${
                    item.read === true ? "read" : "unread"
                  }`}
                  onClick={() => openNotification(item)}
                >
                  <span className="notification-icon">
                    {getNotificationIcon(item.type)}
                  </span>

                  <span className="notification-copy">
                    <strong>{item.title || "Notification"}</strong>
                    <small>{item.message || item.body || "You have an update."}</small>
                    <em>{formatNotificationTime(item.createdAt)}</em>
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
